/**
 * Environment + release metadata for Sentry.
 *
 * Works in both the browser (uses hostname) and the server (uses process.env).
 * Spread the result into `Sentry.init({ ... })` alongside `sentryScrubbers`.
 *
 * Environment values (kept intentionally coarse for spike tracking):
 *   - "production"  → recoverytrauma.com / www.recoverytrauma.com / *.lovable.app published slug
 *   - "preview"     → id-preview--*.lovable.app and *-dev.lovable.app
 *   - "development" → localhost / 127.0.0.1 / sandbox hosts
 *
 * Release value (first non-empty wins):
 *   SENTRY_RELEASE → VITE_SENTRY_RELEASE → CF_PAGES_COMMIT_SHA →
 *   GITHUB_SHA → VITE_APP_VERSION → package.json version → "unknown"
 */

const PRODUCTION_HOSTS = new Set([
  "recoverytrauma.com",
  "www.recoverytrauma.com",
  "recoverytrauma.lovable.app",
]);

function detectEnvironment(): "production" | "preview" | "development" {
  // Server: honour explicit override first.
  const envOverride =
    (typeof process !== "undefined" && process.env?.SENTRY_ENVIRONMENT) ||
    (typeof import.meta !== "undefined" &&
      (import.meta as { env?: Record<string, string | undefined> }).env
        ?.VITE_SENTRY_ENVIRONMENT);
  if (envOverride) {
    const v = envOverride.toLowerCase();
    if (v === "production" || v === "preview" || v === "development") return v;
  }

  // Browser: derive from hostname.
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
      return "development";
    }
    if (PRODUCTION_HOSTS.has(host)) return "production";
    if (host.startsWith("id-preview--") || host.endsWith("-dev.lovable.app")) {
      return "preview";
    }
    // Any other *.lovable.app is a published deploy slug → production.
    if (host.endsWith(".lovable.app")) return "production";
    return "production";
  }

  // Server fallback.
  const nodeEnv = typeof process !== "undefined" ? process.env?.NODE_ENV : undefined;
  if (nodeEnv === "development") return "development";
  return "production";
}

function detectRelease(): string {
  const envs: Array<string | undefined> = [
    typeof process !== "undefined" ? process.env?.SENTRY_RELEASE : undefined,
    typeof import.meta !== "undefined"
      ? (import.meta as { env?: Record<string, string | undefined> }).env
          ?.VITE_SENTRY_RELEASE
      : undefined,
    typeof process !== "undefined" ? process.env?.CF_PAGES_COMMIT_SHA : undefined,
    typeof process !== "undefined" ? process.env?.GITHUB_SHA : undefined,
    typeof import.meta !== "undefined"
      ? (import.meta as { env?: Record<string, string | undefined> }).env
          ?.VITE_APP_VERSION
      : undefined,
  ];
  for (const v of envs) {
    if (v && v.trim()) return `recoverytrauma@${v.trim().slice(0, 12)}`;
  }
  return "recoverytrauma@unknown";
}

export const sentryEnvironment = detectEnvironment();
export const sentryRelease = detectRelease();

/**
 * Spread into `Sentry.init({ ... })` together with `sentryScrubbers`.
 * Also sets an `app` tag so every event is filterable at a glance.
 */
export const sentryContext = {
  environment: sentryEnvironment,
  release: sentryRelease,
  initialScope: {
    tags: {
      app: "recoverytrauma",
      environment: sentryEnvironment,
      release: sentryRelease,
      runtime: typeof window === "undefined" ? "server" : "browser",
    },
  },
};
